/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import dayjs from "dayjs";
import { When } from "react-if";
import styled from "styled-components";
import { FlexContainer, Input, Text } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { useDateValues } from "~/hooks";
import { isEmpty, noop } from "~/tools";
import { DateRange } from "~/types";
import { FilterHeader } from "./FilterHeader";
import { Mode } from "./types";
import { getSelectedMode } from "./utils";

const MIN_DATE = dayjs("2022-01-01").format(dateFormat.shortDate);

type Props = {
  isExcludeSelected?: boolean;
  selectedRange?: Partial<DateRange>;
  label?: string;
  setDefaultEndDateToToday?: boolean;
  allowModeChange?: boolean;
  onModeChange?: (mode: Mode) => void;
  onRangeSelect: (range: DateRange) => void;
};

const handleDefaultEndDate = (
  defaultDate: dayjs.Dayjs,
  startDate: string,
  endDate: string | undefined
) => {
  if (!endDate || dayjs(endDate).isBefore(startDate)) {
    return defaultDate.format(dateFormat.shortDate);
  }
  return endDate;
};

export function SelectableDateRange({
  onRangeSelect,
  selectedRange,
  label,
  setDefaultEndDateToToday = false,
  onModeChange = noop,
  allowModeChange = false,
  isExcludeSelected = false,
}: Props) {
  const { endDate, setEndDate, startDate, setStartDate } =
    useDateValues(selectedRange);
  const defaultDate = dayjs();
  const maxDate = dayjs().format(dateFormat.shortDate);

  const handleStartDateSelection = (value: string) => {
    if (isEmpty(value)) {
      setStartDate(undefined);
      setEndDate(undefined);

      onRangeSelect({
        startDate: undefined,
        endDate: undefined,
      });

      return;
    }

    if (!dayjs(value).isBetween(MIN_DATE, maxDate, "day", "[]")) {
      onRangeSelect({
        startDate: MIN_DATE,
        endDate: undefined,
      });

      return;
    }

    if (setDefaultEndDateToToday) {
      onRangeSelect({
        startDate: value,
        endDate: handleDefaultEndDate(defaultDate, value, endDate),
      });

      return;
    }

    if (dayjs(endDate).isBefore(value)) {
      setEndDate(undefined);

      onRangeSelect({
        startDate: value,
        endDate: undefined,
      });

      return;
    }

    onRangeSelect({
      startDate: value,
      endDate: endDate as string,
    });
  };

  const handleEndDateSelection = (value: string) => {
    if (!dayjs(value).isBetween(startDate, maxDate, "day", "[]")) {
      onRangeSelect({
        startDate,
        endDate: startDate,
      });

      return;
    }

    setEndDate(value);

    onRangeSelect({
      startDate,
      endDate: value,
    });
  };

  return (
    <>
      <FilterHeader
        allowModeChange={allowModeChange}
        defaultMode={getSelectedMode(isExcludeSelected)}
        onModeChange={onModeChange}
      />
      <When condition={label}>
        <Text margin="0.5rem" marginBottom="0">
          {label}
        </Text>
      </When>
      <FlexContainer>
        <StartInputWrap>
          <Input
            aria-label="Start date"
            type="date"
            value={startDate ?? ""}
            min={MIN_DATE}
            max={maxDate}
            onChange={(event) => handleStartDateSelection(event.target.value)}
            small
          />
        </StartInputWrap>

        <EndInputWrap>
          <Input
            aria-label="End date"
            type="date"
            value={endDate ?? ""}
            min={startDate}
            max={maxDate}
            disabled={startDate === undefined}
            onChange={(event) => handleEndDateSelection(event.target.value)}
            small
          />
        </EndInputWrap>
      </FlexContainer>
    </>
  );
}

export const StartInputWrap = styled.div`
  width: 100%;
  padding: 0.5rem;
  padding-right: 0.25rem;
`;

export const EndInputWrap = styled.div`
  width: 100%;
  padding: 0.5rem;
  padding-left: 0.25rem;
`;
